import React, { useState, useEffect } from 'react';
import { XMarkIcon, TruckIcon, UserIcon, CalendarIcon, HashtagIcon, CogIcon } from '@heroicons/react/24/outline';
import FormInput from './FormInput';
import FormSelect from './FormSelect';
import FormTextarea from './FormTextarea';
import useFormValidation from '../../hooks/useFormValidation';
import { validationSchemas, sanitizers, asyncValidators, debounce } from '../../utils/validation';
import apiClient from '../../api/config';

interface Vehicle {
  id?: number;
  customer_id: number;
  make: string;
  model: string;
  year: number;
  color: string;
  plate_number: string;
  vin: string;
  engine_number: string;
  mileage: number;
  fuel_type: string;
  transmission_type: string;
  notes: string;
}

interface Customer {
  id: number;
  name: string;
  email: string;
  phone: string;
}

interface VehicleFormProps {
  vehicle?: Vehicle | null;
  onSubmit: (data: Partial<Vehicle>) => void | Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const VehicleForm: React.FC<VehicleFormProps> = ({
  vehicle,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [plateNumberChecking, setPlateNumberChecking] = useState(false);
  const [vinChecking, setVinChecking] = useState(false);

  const currentYear = new Date().getFullYear();

  const initialData = vehicle || {
    customer_id: 0,
    make: '',
    model: '',
    year: currentYear,
    color: '',
    plate_number: '',
    vin: '',
    engine_number: '',
    mileage: 0,
    fuel_type: 'Petrol',
    transmission_type: 'Automatic',
    notes: '',
  };

  const {
    formData,
    errors,
    touched,
    isValid,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    setError,
    clearError,
  } = useFormValidation({
    validationSchema: validationSchemas.vehicle,
    initialData,
    sanitizers: {
      make: sanitizers.trim,
      model: sanitizers.trim,
      color: sanitizers.trim,
      plate_number: sanitizers.plateNumber,
      vin: sanitizers.vin,
      engine_number: sanitizers.trim,
      notes: sanitizers.trim,
    },
    onSubmit: async (data) => {
      await onSubmit(data);
    },
    validateOnChange: true,
    validateOnBlur: true,
  });

  // Fetch customers on component mount
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await apiClient.get('/customers');
        setCustomers(response.data.data || []);
      } catch (error) {
        console.error('Failed to fetch customers:', error);
        // Mock data for development
        setCustomers([
          { id: 1, name: 'Ahmed Al-Rashid', email: 'ahmed@example.com', phone: '+965 1234 5678' },
          { id: 2, name: 'Fatima Hassan', email: 'fatima@example.com', phone: '+965 2345 6789' },
          { id: 3, name: 'Mohammed Ali', email: 'mohammed@example.com', phone: '+965 3456 7890' },
        ]);
      } finally {
        setLoadingCustomers(false);
      }
    };

    fetchCustomers();
  }, []);

  // Async validation for plate number uniqueness
  const checkPlateNumberUnique = debounce(async (plateNumber: string) => {
    if (!plateNumber || plateNumber.length < 3) return;

    setPlateNumberChecking(true);
    try {
      const isUnique = await asyncValidators.uniquePlateNumber(plateNumber, vehicle?.id);
      if (!isUnique) {
        setError('plate_number', 'This plate number is already registered');
      } else {
        clearError('plate_number');
      }
    } catch (error) {
      console.error('Plate number validation error:', error);
    } finally {
      setPlateNumberChecking(false);
    }
  }, 500);

  // Async validation for VIN uniqueness
  const checkVinUnique = debounce(async (vin: string) => {
    if (!vin || vin.length < 17) return;

    setVinChecking(true);
    try {
      const isUnique = await asyncValidators.uniqueVin(vin, vehicle?.id);
      if (!isUnique) {
        setError('vin', 'This VIN is already registered');
      } else {
        clearError('vin');
      }
    } catch (error) {
      console.error('VIN validation error:', error);
    } finally {
      setVinChecking(false);
    }
  }, 500);

  // Watch for changes in plate number and VIN for async validation
  useEffect(() => {
    if (touched.plate_number && formData.plate_number) {
      checkPlateNumberUnique(formData.plate_number);
    }
  }, [formData.plate_number, touched.plate_number]);

  useEffect(() => {
    if (touched.vin && formData.vin) {
      checkVinUnique(formData.vin);
    }
  }, [formData.vin, touched.vin]);

  // Options for form fields
  const customerOptions = customers.map(customer => ({
    value: customer.id,
    label: `${customer.name} (${customer.phone})`,
  }));

  const fuelTypeOptions = [
    { value: 'Petrol', label: 'Petrol' },
    { value: 'Diesel', label: 'Diesel' },
    { value: 'Hybrid', label: 'Hybrid' },
    { value: 'Electric', label: 'Electric' },
  ];

  const transmissionTypeOptions = [
    { value: 'Automatic', label: 'Automatic' },
    { value: 'Manual', label: 'Manual' },
    { value: 'CVT', label: 'CVT' },
  ];

  // Generate year options
  const yearOptions = [];
  for (let year = currentYear + 1; year >= 1900; year--) {
    yearOptions.push({ value: year, label: year.toString() });
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <TruckIcon className="w-6 h-6 mr-2 text-[#008B8B]" />
              {vehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
            </h2>
            <button
              onClick={onCancel}
              disabled={isSubmitting}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Customer Selection */}
            <FormSelect
              label="Customer"
              placeholder="Select a customer"
              required
              options={customerOptions}
              loading={loadingCustomers}
              {...getFieldProps('customer_id')}
            />

            {/* Vehicle Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormInput
                label="Make"
                placeholder="e.g., Toyota, Honda, BMW"
                required
                leftIcon={<TruckIcon className="w-5 h-5" />}
                loading={isSubmitting}
                {...getFieldProps('make')}
              />

              <FormInput
                label="Model"
                placeholder="e.g., Camry, Accord, 320i"
                required
                leftIcon={<TruckIcon className="w-5 h-5" />}
                loading={isSubmitting}
                {...getFieldProps('model')}
              />

              <FormSelect
                label="Year"
                required
                options={yearOptions}
                loading={isSubmitting}
                {...getFieldProps('year')}
              />

              <FormInput
                label="Color"
                placeholder="e.g., Silver, Black, White"
                required
                leftIcon={<div className="w-5 h-5 rounded-full border-2 border-gray-400"></div>}
                loading={isSubmitting}
                {...getFieldProps('color')}
              />
            </div>

            {/* Vehicle Identification */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormInput
                label="Plate Number"
                placeholder="e.g., ABC 123"
                required
                leftIcon={<HashtagIcon className="w-5 h-5" />}
                loading={isSubmitting || plateNumberChecking}
                {...getFieldProps('plate_number')}
              />

              <FormInput
                label="VIN (Vehicle Identification Number)"
                placeholder="17-character VIN"
                leftIcon={<HashtagIcon className="w-5 h-5" />}
                loading={isSubmitting || vinChecking}
                maxLength={17}
                {...getFieldProps('vin')}
              />

              <FormInput
                label="Engine Number"
                placeholder="Engine serial number"
                leftIcon={<CogIcon className="w-5 h-5" />}
                loading={isSubmitting}
                {...getFieldProps('engine_number')}
              />

              <FormInput
                label="Current Mileage (km)"
                type="number"
                placeholder="0"
                required
                min={0}
                max={9999999}
                leftIcon={<span className="text-gray-400 font-medium">KM</span>}
                loading={isSubmitting}
                {...getFieldProps('mileage')}
              />
            </div>

            {/* Vehicle Specifications */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormSelect
                label="Fuel Type"
                required
                options={fuelTypeOptions}
                loading={isSubmitting}
                {...getFieldProps('fuel_type')}
              />

              <FormSelect
                label="Transmission Type"
                required
                options={transmissionTypeOptions}
                loading={isSubmitting}
                {...getFieldProps('transmission_type')}
              />
            </div>

            {/* Notes */}
            <FormTextarea
              label="Notes"
              placeholder="Additional notes about the vehicle (service history, special requirements, etc.)"
              rows={4}
              maxLength={1000}
              showCharCount
              loading={isSubmitting}
              {...getFieldProps('notes')}
            />

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onCancel}
                disabled={isSubmitting}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isValid || isSubmitting || loading || plateNumberChecking || vinChecking}
                className="px-4 py-2 bg-[#008B8B] text-white rounded-md hover:bg-[#006666] disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {(isSubmitting || loading) && (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                )}
                {vehicle ? 'Update Vehicle' : 'Add Vehicle'}
              </button>
            </div>
          </form>

          {/* Form Summary (Development Helper) */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Form State</h3>
              <div className="text-xs space-y-1">
                <div>Valid: <span className={isValid ? 'text-green-600' : 'text-red-600'}>{isValid ? 'Yes' : 'No'}</span></div>
                <div>Errors: {Object.keys(errors).length}</div>
                <div>Touched: {Object.keys(touched).filter(key => touched[key]).length}</div>
                <div>Async Checks: {(plateNumberChecking || vinChecking) ? 'Running...' : 'Complete'}</div>
                {Object.keys(errors).length > 0 && (
                  <div className="mt-2">
                    <div className="text-red-600">Errors:</div>
                    {Object.entries(errors).map(([field, error]) => (
                      <div key={field} className="ml-2">• {field}: {typeof error === 'string' ? error : (error as any)?.message || 'Invalid value'}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehicleForm;